/**
 * Guided-tour chapter definitions.
 *
 * Each chapter is a short, replayable walkthrough of the REAL admin UI -
 * coachmarks anchored to actual elements, not a slideshow about the product.
 * The SetupGuide widget lists chapters and tracks per-chapter completion in
 * the same `onboarding_progress` payload the welcome wizard already uses.
 *
 * Step shape:
 *   route     - path the step lives on; the engine navigates there first.
 *               Omit for steps that keep whatever page is current.
 *   selector  - CSS selector to spotlight. Omit for a centered card
 *               (welcome / wrap-up steps).
 *   optional  - true = if the selector never appears, skip the step silently
 *               (used for elements that only exist after a user action,
 *               e.g. the slide-out create panel).
 *   advanceOn - "click" = clicking the highlighted element itself also
 *               advances the tour (so doing the real thing IS progressing).
 *   title/body - popover copy. Keep bodies to two short sentences; admins
 *               are reading these mid-task.
 *
 * Keep step counts per chapter in the 5-8 range. A long mandatory tour gets
 * abandoned and the admin never sees the rest of the product.
 *
 * Every chapter is listed by the Setup guide. (The welcome wizard's
 * "two-minute tour" chapter was removed on 5 Oct 2026 with its link.)
 *
 * Chapter ids MUST contain at least one letter (never purely numeric, never
 * "0"): PHP casts numeric JSON object keys to int array keys, which both
 * trips the API's string-id validation and can make json_encode emit a JSON
 * ARRAY - which the FE deliberately reads as "no chapters".
 */

export const CHAPTERS = {
  people: {
    id: "people",
    title: "People and access",
    blurb: "Add and import people, group them, archive leavers.",
    minutes: 2,
    steps: [
      {
        title: "Getting people in",
        body:
          "Everything starts with people. This covers adding them one by one, " +
          "importing a spreadsheet, grouping them, and what happens when someone leaves."
      },
      {
        route: "/admin/users",
        selector: "[data-tour='nav-users']",
        title: "Where people live",
        body:
          "User Management is the full list of everyone in your account. " +
          "Search it, sort it, and open anyone to see their training."
      },
      {
        route: "/admin/users",
        selector: "[data-tour='add-user']",
        advanceOn: "click",
        title: "Adding one person",
        body:
          "A name and an email is enough. They get a welcome email with a " +
          "set-password link, so you never handle anyone's password.",
        disabledBody:
          "Your account is at its user limit, so adding is disabled here. " +
          "Press Next to carry on."
      },
      {
        // Shown only if they clicked Add User: the whole panel, so its own
        // buttons work, and the tour moves on when it closes. Without this
        // step the tour jumped to Import Users with the panel still open
        // over that button (17 Sep 2026).
        selector: ".slideout-panel .slideout",
        optional: true,
        advanceOnGone: true,
        title: "Just a name and an email",
        body:
          "That's all a user needs. Save it, or close the panel, and the " +
          "tour carries on."
      },
      {
        // Only the non-HR variant carries this anchor. Companies on an HR
        // integration render ImportUsersHR instead, so this step stands down.
        route: "/admin/users",
        selector: "[data-tour='import-users']",
        optional: true,
        title: "Or bring a whole spreadsheet",
        body:
          "Import Users takes a CSV, so a new intake is one upload rather " +
          "than fifty forms."
      },
      {
        route: "/admin/groups",
        selector: "[data-tour='nav-groups']",
        title: "Groups keep it manageable",
        body:
          "A group is a set of people you treat the same way, like a site or " +
          "a department. Assign training to the group and everyone in it gets it."
      },
      {
        route: "/admin/groups",
        selector: "[data-tour='add-group']",
        advanceOn: "click",
        title: "Make your first group",
        body:
          "Name it after something real, like Warehouse or New Starters. " +
          "You can move people in and out at any time."
      },
      {
        selector: ".slideout-panel .slideout",
        optional: true,
        advanceOnGone: true,
        title: "A name is enough to start",
        body:
          "Save the group, or close the panel, and the tour carries on. " +
          "People can be added to it at any time."
      },
      {
        route: "/admin/archived-users",
        selector: "[data-tour='nav-archived-users']",
        title: "When someone leaves",
        body:
          "Archiving frees the licence but keeps their training record, so an " +
          "audit still has the proof. You can restore them if they come back."
      },
      {
        title: "People, done",
        body:
          "You can add, import, group and archive. Next up is what you " +
          "actually give them: the training itself."
      }
    ]
  },

  content: {
    id: "content",
    title: "Training content",
    blurb: "Your course library, your own courses, and playlists.",
    minutes: 2,
    steps: [
      {
        title: "What you give people",
        body:
          "Your account comes with a ready-made library, and you can add your " +
          "own courses and bundle them into playlists."
      },
      {
        route: "/admin/courses",
        selector: "[data-tour='nav-courses']",
        title: "The full library",
        body:
          "Every course available to your company, included ones and anything " +
          "you have built. Opening one shows exactly what a learner sees."
      },
      {
        route: "/admin/courses",
        selector: "[data-tour='add-course']",
        advanceOn: "click",
        title: "Build your own",
        body:
          "Add New Course lets you upload your own material and add a quiz. " +
          "Your induction or site rules can live here alongside the ready-made ones."
      },
      {
        selector: ".slideout-panel .slideout",
        optional: true,
        advanceOnGone: true,
        title: "Your course, your way",
        body:
          "Give it a name to start, add the material whenever you are ready. " +
          "Save it, or close the panel, and the tour carries on."
      },
      {
        route: "/admin/playlists",
        selector: "[data-tour='nav-playlists']",
        title: "Playlists bundle courses",
        body:
          "A playlist is an ordered set of courses, like an induction pathway. " +
          "Assign the playlist and the whole sequence goes out at once."
      },
      {
        route: "/admin/playlists",
        selector: "[data-tour='add-playlist']",
        advanceOn: "click",
        title: "Make a pathway",
        body:
          "Add New Playlist, then pick the courses and the order. Pair it with " +
          "a group and a new starter gets everything automatically."
      },
      {
        selector: ".slideout-panel .slideout",
        optional: true,
        advanceOnGone: true,
        title: "Courses in order",
        body:
          "Name the playlist and pick the courses in the order people should " +
          "take them. Save it, or close the panel, and the tour carries on."
      },
      {
        title: "Content, done",
        body:
          "Library, your own courses, and playlists. Last piece is proving " +
          "any of it happened."
      }
    ]
  },

  tracking: {
    id: "tracking",
    title: "Reports and proof",
    blurb: "Every report type, and how to get evidence out.",
    minutes: 2,
    steps: [
      {
        title: "Proving it happened",
        body:
          "Completion is only useful if you can show it. Every report here " +
          "can be exported when someone asks for evidence."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='nav-reports']",
        title: "All your reporting",
        body:
          "One page, one card per question you might be asked. Start from the " +
          "question, not the data."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='report-user']",
        title: "By person",
        body:
          "What has this person done? Use it for a one-to-one, or when " +
          "someone needs their own record."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='report-course']",
        title: "By course",
        body:
          "Who has done this course? This is the one for chasing an " +
          "outstanding compliance module."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='report-playlist']",
        title: "By playlist",
        body:
          "Progress through a whole pathway rather than a single course. " +
          "There is a group report alongside it for the same view by team."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='report-external']",
        title: "Training done elsewhere",
        body:
          "External training records courses taken outside IMPROVE, so a " +
          "first-aid certificate from a third party still shows up."
      },
      {
        route: "/admin/reports",
        selector: "[data-tour='report-platform']",
        title: "Everything at once",
        body:
          "The full platform report is the whole account in one export. " +
          "This is usually the one an auditor actually wants."
      },
      {
        title: "Proof, done",
        body:
          "By person, by course, by pathway, by team, and the lot. " +
          "One last chapter: making the account yours."
      }
    ]
  },

  account: {
    id: "account",
    title: "Account and branding",
    blurb: "Certificates, notifications, company details, your plan.",
    minutes: 2,
    steps: [
      {
        title: "Making it yours",
        body:
          "The last stop is settings: what your certificates look like, what " +
          "emails go out, and where your plan lives."
      },
      {
        route: "/admin/settings",
        selector: "[data-tour='nav-settings']",
        title: "Settings",
        body:
          "Everything that changes how IMPROVE behaves for your company " +
          "rather than for one person."
      },
      {
        route: "/admin/settings",
        selector: "[data-tour='settings-tabs']",
        title: "Certificates and notifications",
        body:
          "Notification controls the emails your people receive. Certificate " +
          "controls what their completion certificate looks like."
      },
      {
        // Billing nav renders only on Direct for admin-level users
        // (APP_NAME === 'direct' && isAdminLevel), so it stands down elsewhere.
        route: "/admin/finance/company",
        selector: "[data-tour='nav-company']",
        optional: true,
        title: "Your company details",
        body:
          "Name, address and contact details. These appear on certificates " +
          "and invoices, so they are worth getting right early."
      },
      {
        route: "/admin/finance/account-status",
        selector: "[data-tour='nav-account-status']",
        optional: true,
        title: "Your plan",
        body:
          "Your licence count and renewal date. Check here first if adding a " +
          "user is ever refused."
      },
      {
        // Centered card, NOT anchored to [data-tour='setup-guide']: SetupGuide
        // hides itself while a tour is running (visible() checks
        // !tour.tourActive()), so a step spotlighting the fab can never
        // resolve and the engine skips it silently. Describe it instead - the
        // fab is back the instant the tour ends. Same reason core's wrap-up
        // step has no selector.
        title: "That is the whole product",
        body:
          "People, content, proof and settings. The Setup guide button in the " +
          "bottom-right corner keeps every chapter, so you can replay any of " +
          "them whenever you need to."
      }
    ]
  }

  // The registry IS the tour: adding a chapter is data, not UI. Keep step
  // counts in the 5-8 range, and give every selector an anchor that really
  // exists - a missing one is SKIPPED SILENTLY by the engine, so a typo
  // costs a step without ever raising an error. e2e/tour-chapters-e2e.js
  // walks every chapter and fails if a declared step never appears.
};

export function getChapter(id) {
  return CHAPTERS[id] || null;
}

// The Setup guide's list, in definition order.
export function chapterList() {
  return Object.keys(CHAPTERS).map(function(id) {
    return CHAPTERS[id];
  });
}
